import { ExtractResponseType } from "@hyper-fetch/core";
import { useFetch } from "@hyper-fetch/react";
import { useCallback } from "react";

import { sdk } from "@/api/sdk";

type PermissionString = `${string}:${string}`;

function parsePermission(permission: string): { action: string; subject: string } {
  const separatorIndex = permission.indexOf(":");
  if (separatorIndex === -1) {
    return { action: permission, subject: "all" };
  }
  return {
    action: permission.slice(0, separatorIndex),
    subject: permission.slice(separatorIndex + 1),
  };
}

// The me cache entry can vanish while the session is alive: hyper-fetch
// garbage-collects it after `cacheTime` (5 min default), and signing out wipes
// it while screens are still mounted. A useUser instance that MOUNTS inside
// such a window (e.g. opening Settings right after the entry was collected)
// starts with no data and would crash, so the last known session user is kept
// at module scope and served until the background refetch lands. The throw
// only remains for the never-signed-in case, which the route gates prevent.
let lastKnownUser: ExtractResponseType<typeof sdk.users.me.$get> | null = null;

export const useUser = () => {
  const { data, refetch } = useFetch(sdk.users.me.$get);

  if (data) {
    lastKnownUser = data;
  }
  const userData = data ?? lastKnownUser;

  if (!userData) {
    throw new Error("User data not found");
  }
  const permissions: PermissionString[] | null = userData.permissions ?? null;
  const role = userData.role ?? null;

  const hasPermission = useCallback(
    (action: string, subject: string): boolean => {
      if (!permissions) return false;

      return permissions.some((p) => {
        const parsed = parsePermission(p);
        if (parsed.action === "manage" && parsed.subject === "all") return true;
        if (parsed.action === "manage" && parsed.subject === subject) return true;
        return parsed.action === action && parsed.subject === subject;
      });
    },
    [permissions],
  );

  const fullName = `${userData.user.firstName || ""}${userData.user.firstName ? " " : ""}${userData.user.lastName || ""}`;
  const initials = `${userData.user.firstName?.[0] ?? ""}${userData.user.lastName?.[0] ?? ""}`;

  return {
    ...userData,
    fullName,
    initials,
    permissions,
    role,
    hasPermission,
    refetch: async () => {
      await refetch();
    },
  };
};
